import { Box, Stack, Typography, styled } from "@mui/material";
import { ReactNode, useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { CollapsibleCard } from "@/components/elements/CollapsibleCard";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { Input } from "@/modules/irohana-components/elements/Input";
import { InvoicePreview } from "@/features/organization/invoice/components/invoice-form/InvoicePreview";
import { TInvoiceForm } from "@/features/organization/invoice/types/InvoiceForm";
import { background, divider, text } from "@/theme/colors";

type TFormatCodeSectionProps = {
  title: string;
  description: ReactNode;
  /** Form field holding the editable format string. */
  name: keyof TInvoiceForm;
  /** Insertable code tokens (rendered as `{code}` chips). */
  codes: string[];
  /** Default format restored when the field is emptied (spec No.41 / No.48). */
  defaultFormat: string;
  /** Static preview shown under the chips. */
  previewValue: string;
};

const isSequence = (code: string) => code.startsWith("連番");

/**
 * Editable layout for 請求書番号設定 — a format Input, insertable
 * `{code}` chips (inserted at the caret), and a preview box.
 *
 * Chip rules (spec No.41): a chip already present is disabled; once any 連番 is inserted, all
 * 連番 chips are disabled (only one allowed). Emptying the field restores the default on blur.
 */
export const FormatCodeSection = ({ title, description, name, codes, defaultFormat, previewValue }: TFormatCodeSectionProps) => {
  const { control, getValues, setValue } = useFormContext<TInvoiceForm>();
  const value = (useWatch({ control, name }) as string) ?? "";
  const hasSequence = codes.some((code) => isSequence(code) && value.includes(`{${code}}`));

  const insertCode = useCallback(
    (code: string) => {
      const token = `{${code}}`;
      const el = document.querySelector<HTMLInputElement>(`input[name="${name}"]`);
      const current = (getValues(name) as string) ?? "";
      const caret = el?.selectionStart ?? current.length;
      const next = `${current.slice(0, caret)}${token}${current.slice(caret)}`;
      setValue(name, next, { shouldDirty: true, shouldValidate: true });
    },
    [getValues, setValue, name],
  );

  // Empty field → restore the default format (blur fires as focusout, which bubbles).
  const handleBlur = () => {
    if (!(getValues(name) as string)) {
      setValue(name, defaultFormat, { shouldDirty: true, shouldValidate: true });
    }
  };

  const isChipDisabled = (code: string) => (isSequence(code) ? hasSequence : value.includes(`{${code}}`));

  return (
    <CollapsibleCard title={title} description={description}>
      <Stack gap={2}>
        <Stack gap={1}>
          <Box onBlur={handleBlur}>
            <FormField control={control} name={name} noLabel>
              <Input placeholder="入力してください" />
            </FormField>
          </Box>

          <Typography variant="cap12" color={text.secondary}>
            テキストボックス内のカーソル位置にコードを挿入できます。
          </Typography>

          <Stack direction="row" flexWrap="wrap" gap={1}>
            {codes.map((code) => {
              const disabled = isChipDisabled(code);
              return (
                <CodeChip key={code} type="button" disabled={disabled} onClick={() => insertCode(code)}>
                  {`{${code}}`}
                </CodeChip>
              );
            })}
          </Stack>
        </Stack>

        <InvoicePreview value={previewValue} />
      </Stack>
    </CollapsibleCard>
  );
};

const CodeChip = styled("button")`
  min-width: 76px;
  border: 1px solid ${divider.high};
  border-radius: 12px;
  background: ${background.white};
  color: ${text.primary};
  font-size: 12px;
  line-height: 1.5;
  text-align: center;
  padding: 4px 8px;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover {
    background: ${background.primary};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
    background: ${background.disable};
  }
`;
